import { BridgeComponent } from '@hotwired/hotwire-native-bridge'

/**
 * The timeout for the bridge requests
 *
 * This is used to prevent the bridge from hanging indefinitely in case it
 * doesn't receive a response from the native app.
 */
const BRIDGE_TIMEOUT = 5000

interface SetTokenRequest {
  authToken: string
}

interface TokenResponse {
  authToken: string
  deviceToken: string
}

interface HasTokenResponse {
  tokenPresent: boolean
}

/**
 * Base controller for authentication token management
 *
 * This controller provides methods for getting and setting authentication tokens
 * and checking if a token is present.
 *
 */
export default class AuthenticationTokenBaseController<
  T extends HTMLElement,
> extends BridgeComponent<T> {
  static component = 'bridge--authentication-token'

  /**
   * Gets the authentication token for the native app
   */
  async getToken(): Promise<TokenResponse | undefined> {
    const response = await Promise.race([
      this.sendPromise<{}, { data: TokenResponse }>('getToken', {}),
      timeoutPromise(undefined),
    ])

    if (!response) {
      return undefined
    }

    return response.data
  }

  /**
   * Sets the authentication token for the native app
   */
  async setAuthToken(authToken: string): Promise<TokenResponse | undefined> {
    const response = await Promise.race([
      this.sendPromise<SetTokenRequest, { data: TokenResponse }>('setToken', { authToken }),
      timeoutPromise(undefined),
    ])

    if (!response) {
      return undefined
    }

    return response.data
  }

  /**
   * Checks if an authentication token is present
   */
  async hasToken(): Promise<boolean> {
    const response = await Promise.race([
      this.sendPromise<{}, { data: HasTokenResponse }>('hasToken', {}),
      timeoutPromise(undefined),
    ])

    if (!response) {
      return false
    }

    return response.data.tokenPresent
  }

  /**
   * Deletes the authentication token
   */
  async deleteToken(): Promise<void> {
    await Promise.race([
      this.sendPromise<{}, { data: void }>('deleteToken', {}),
      timeoutPromise(undefined),
    ])
  }

  sendPromise<Data = Record<string, unknown>, Result = Record<string, unknown>>(
    event: string,
    data: Data,
  ): Promise<Result> {
    return new Promise((resolve) => {
      this.send<Data, Result>(event, data, resolve)
    })
  }
}

function timeoutPromise<T>(defaultValue: T): Promise<T> {
  return new Promise<T>((resolve) => setTimeout(() => resolve(defaultValue), BRIDGE_TIMEOUT))
}
